import apiClient from "@/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { logger } from "@/lib/default-logger";
import { toast } from "@/components/core/toaster";

import { Field, fieldsKeys } from "./use-fields";
import { groupsKeys } from "./use-groups";

export interface Contact {
	id: string;
	phone: string;
	attributes: any;
	tags: string[];
	createdAt: string;
	updatedAt: string;
}

export interface FileMapping {
	sessionId: string;
	fileName: string;
	rowCount: number;
	columns: {
		index: number;
		name: string;
	}[];
	preview: string[][];
}

export interface ContactsResponse {
	data: Contact[];
	total: number;
}

export interface ContactFormData {
	phone: string;
	attributes: Record<string, any>;
	tags: string[];
}

export const contactsKeys = {
	all: ["contacts"] as const,
	lists: () => [...contactsKeys.all, "list"] as const,
	list: (query?: string) => [...contactsKeys.lists(), { query }] as const,
	details: () => [...contactsKeys.all, "detail"] as const,
	detail: (id: string) => [...contactsKeys.details(), id] as const,
	calls: (contactId?: string) => [...contactsKeys.all, "calls", ...(contactId ? [contactId] : [])] as const,
	call: (contactId: string, id: string) => [...contactsKeys.calls(contactId), id] as const,
	count: () => [...contactsKeys.all, "count"] as const,
};

export function useContacts(query?: string) {
	return useQuery({
		queryKey: contactsKeys.list(query),
		queryFn: async (): Promise<ContactsResponse> => {
			const queryString = query ? `?${query}` : "";
			const response = await apiClient.get(`/crm/contacts${queryString}`);
			return response.data;
		},
	});
}

export function useContact(id: string) {
	return useQuery({
		queryKey: contactsKeys.detail(id),
		queryFn: async (): Promise<Contact> => {
			const response = await apiClient.get(`/crm/contacts/${id}`);
			return response.data;
		},
		enabled: !!id,
	});
}

export function useContactFields() {
	return useQuery({
		queryKey: fieldsKeys.lists(),
		queryFn: async () => {
			const response = await apiClient.get("/crm/fields");
			return response.data.filter((field: Field) => !field?.system);
		},
	});
}

export function useCreateContact() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (data: ContactFormData): Promise<Contact> => {
			const response = await apiClient.post("/crm/contacts", {
				phone: data.phone,
				attributes: data.attributes,
				tags: data.tags,
			});
			return response.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: contactsKeys.lists() });
			toast.success("Contact created successfully");
		},
		onError: (error: any) => {
			logger.error(error);
			const errorMessage = error?.response?.data?.message;
			toast.error(Array.isArray(errorMessage) ? errorMessage[0] : errorMessage || "Something went wrong!");
		},
	});
}

export function useUpdateContact() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ id, data }: { id: string; data: ContactFormData }): Promise<Contact> => {
			const response = await apiClient.put(`/crm/contacts/${id}`, {
				phone: data.phone,
				attributes: data.attributes,
				tags: data.tags,
			});
			return response.data;
		},
		onSuccess: (data, variables) => {
			queryClient.setQueryData(contactsKeys.detail(variables.id), data);
			queryClient.invalidateQueries({ queryKey: contactsKeys.lists() });
			toast.success("Contact updated successfully");
		},
		onError: (error: any) => {
			logger.error(error);
			const errorMessage = error?.response?.data?.message;
			toast.error(Array.isArray(errorMessage) ? errorMessage[0] : errorMessage || "Something went wrong!");
		},
	});
}

export function useDeleteContact() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (id: string): Promise<void> => {
			await apiClient.delete(`/crm/contacts/${id}`);
		},
		onSuccess: (_, id) => {
			queryClient.removeQueries({ queryKey: contactsKeys.detail(id) });
			queryClient.invalidateQueries({ queryKey: contactsKeys.lists() });
			toast.success("Contact deleted successfully");
		},
		onError: (error: any) => {
			logger.error(error);
			toast.error("Something went wrong!");
		},
	});
}

export function useImportContacts() {
	return useMutation({
		mutationFn: async (file: File) => {
			const formData = new FormData();
			formData.append("file", file);

			const response = await apiClient.post("/crm/import/upload", formData, {
				headers: {
					"Content-Type": "multipart/form-data",
				},
			});
			return response.data;
		},
		onError: (error: any) => {
			logger.error(error);
			toast.error("Import failed");
		},
	});
}

export function useProcessImport() {
	return useMutation({
		mutationFn: async ({
			sessionId,
			phoneColumn,
			attributeMapping,
			hasHeaders,
			duplicateStrategy,
			tags,
			groupId,
			newGroupName,
		}: {
			sessionId: string;
			phoneColumn: number;
			attributeMapping: Record<string, number>;
			hasHeaders: boolean;
			duplicateStrategy: string;
			tags: string[];
			groupId: string | null;
			newGroupName: string;
		}) => {
			const response = await apiClient.post(`/crm/import/${sessionId}/process`, {
				phoneColumn,
				attributeMapping,
				hasHeaders,
				duplicateStrategy,
				tags,
				groupId,
				newGroupName,
			});
			return response.data;
		},
		onSuccess: () => {
			// queryClient.invalidateQueries({ queryKey: contactsKeys.lists() });
			toast.success("Contacts import has started. This process may take some time to complete.");
		},
		onError: (error: any) => {
			logger.error(error);
			const errorMessage = error?.response?.data?.message;
			toast.error(Array.isArray(errorMessage) ? errorMessage[0] : errorMessage || "Import processing failed");
		},
	});
}

export function useAttachToGroup() {
  const queryClient = useQueryClient();
  
	return useMutation({
		mutationFn: async (data: { groupId: string; contactIds: string[] }) => {
			const response = await apiClient.post(`/crm/groups/${data.groupId}/attach`, data);
			return response.data;
		},
		onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contactsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: groupsKeys.lists() });
			toast.success("Contacts attached to group successfully");
		},
		onError: (error: any) => {
			logger.error(error);
			toast.error(error.response.data.message || "Something went wrong!");
		},
	});
}

export function useDetachFromGroup() {
  const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (data: { groupId: string; contactIds: string[] }) => {
			const response = await apiClient.post(`/crm/groups/${data.groupId}/detach`, data);
			return response.data;
		},
		onSuccess: () => {
			toast.success("Contacts detached from group successfully");
      queryClient.invalidateQueries({ queryKey: contactsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: groupsKeys.lists() });
		},
		onError: (error: any) => {
			logger.error(error);
			toast.error(error.response.data.message || "Something went wrong!");
		},
	});
}

export function useContactsCount() {
  return useQuery({
		queryKey: contactsKeys.count(),
		queryFn: async (): Promise<number> => {
			const response = await apiClient.get(`/crm/contacts?limit=1&offset=0`);
			return response.data.total;
		},
		refetchOnMount: "always",
	});
}